import * as React from "react"

import { Category } from "@/lib/types"
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"

import { useMenuItemDialog } from "../hooks/use-menu-item-dialog"
import { MenuItemDetailContent } from "./menu-item-detail-content"
import { MenuItemDetailSkeleton } from "./menu-item-detail-skeleton"

interface MenuItemDetailDialogProps {
  selectedItemId: number | null
  selectedItemIndex?: number | null
  setSelectedItemId: (id: number | null) => void
  setSelectedItemIndex?: (index: number | null) => void
  selectedItem: Category["items"][number] | null
  isEditing?: boolean
  restaurantId: string
  afterSubmit?: () => void
  isRestoClosed?: boolean
}

/**
 * Dialog for displaying a menu item's details and customization options
 */
export function MenuItemDetailDialog({
  selectedItemId,
  selectedItemIndex,
  setSelectedItemId,
  setSelectedItemIndex,
  selectedItem: placeholder,
  isEditing = false,
  restaurantId,
  afterSubmit,
  isRestoClosed,
}: MenuItemDetailDialogProps) {
  // Use the custom hook to handle all the logic
  const {
    form,
    scrollAreaRef,
    hasSubmitted,
    invalidCustomizations,
    quantity,
    setQuantity,
    selectedItem,
    showSkeleton,
    handleSubmit,
    calculateTotal,
    addToCartMutation,
  } = useMenuItemDialog({
    selectedItemId,
    setSelectedItemId,
    selectedItemIndex,
    setSelectedItemIndex,
    selectedItem: placeholder,
    isEditing,
    restaurantId,
  })

  const handleSubmitWithCallback = React.useCallback(
    (e: React.FormEvent) => {
      handleSubmit(e)
      afterSubmit?.()
    },
    [handleSubmit, afterSubmit]
  )

  return (
    <Dialog
      open={selectedItemId !== null}
      onOpenChange={(open) => {
        if (!open) {
          setSelectedItemId(null)
          setSelectedItemIndex?.(null)
        }
      }}
    >
      <DialogContent className="lg:max-w-[1000px] flex p-0 pb-5 lg:pl-5 lg:pt-14 max-h-[95vh] overflow-y-auto max-w-[600px] overflow-x-hidden">
        <DialogTitle className="sr-only">{selectedItem?.item_name}</DialogTitle>

        {showSkeleton ? (
          <MenuItemDetailSkeleton />
        ) : (
          selectedItem && (
            <MenuItemDetailContent
              selectedItem={selectedItem}
              form={form}
              scrollAreaRef={scrollAreaRef as React.RefObject<HTMLDivElement>}
              hasSubmitted={hasSubmitted}
              invalidCustomizations={invalidCustomizations}
              quantity={quantity}
              setQuantity={setQuantity}
              handleSubmit={handleSubmitWithCallback}
              calculateTotal={calculateTotal}
              isSubmitting={addToCartMutation.isPending}
              isEdit={isEditing}
              isRestoClosed={isRestoClosed}
            />
          )
        )}
      </DialogContent>
    </Dialog>
  )
}
